import { createSlice } from "@reduxjs/toolkit";
import { clearMeetingState } from "@store/meetingSlice";

const initialState = {
  messages: [],
  unreadCount: 0,
  joinedAt: null,
  panelActive: false,
  connectionStatus: "connected",
  backlogLoaded: false,
};

const isPendingOrFailed = (message) =>
  message.status === "sending" || message.status === "failed";

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    backlogLoaded: (state, action) => {
      const { messages, profileId } = action.payload;
      const serverRows = (messages || []).map((message) => ({
        ...message,
        status: "sent",
        isOwn: message.senderId === profileId,
      }));

      const serverMessageIds = new Set(serverRows.map((message) => message.id));
      const serverClientMessageIds = new Set(
        serverRows.map((message) => message.clientMessageId).filter(Boolean)
      );
      const isFirstLoad = !state.backlogLoaded;
      const localRowsNotOnServer = state.messages
        .filter((message) => isFirstLoad || isPendingOrFailed(message))
        .filter(
          (message) =>
            !serverMessageIds.has(message.id) &&
            !serverClientMessageIds.has(message.clientMessageId)
        );

      state.messages = [...serverRows, ...localRowsNotOnServer];

      if (isFirstLoad) {
        state.joinedAt = Date.now();
        state.backlogLoaded = true;
      }
    },

    messageReceived: (state, action) => {
      const { message, profileId } = action.payload;
      const alreadyPresent = state.messages.some(
        (existing) => existing.id === message.id
      );
      if (alreadyPresent) return;

      state.messages.push({
        ...message,
        status: "sent",
        isOwn: message.senderId === profileId,
      });

      if (!state.panelActive) {
        state.unreadCount += 1;
      }
    },

    ownMessagePending: (state, action) => {
      state.messages.push({
        ...action.payload,
        status: "sending",
        isOwn: true,
      });
    },

    ownMessageConfirmed: (state, action) => {
      const { clientMessageId, message } = action.payload;
      const index = state.messages.findIndex(
        (existing) => existing.clientMessageId === clientMessageId
      );
      if (index === -1) return;

      state.messages[index] = {
        ...message,
        status: "sent",
        isOwn: true,
      };
    },

    ownMessageFailed: (state, action) => {
      const { clientMessageId, errorCode } = action.payload;
      const message = state.messages.find(
        (existing) => existing.clientMessageId === clientMessageId
      );
      if (!message) return;

      message.status = "failed";
      message.errorCode = errorCode;
    },

    ownMessageDiscarded: (state, action) => {
      const { clientMessageId } = action.payload;
      state.messages = state.messages.filter(
        (existing) => existing.clientMessageId !== clientMessageId
      );
    },

    ownMessageRetrying: (state, action) => {
      const { clientMessageId } = action.payload;
      const message = state.messages.find(
        (existing) => existing.clientMessageId === clientMessageId
      );
      if (!message) return;

      message.status = "sending";
      delete message.errorCode;
    },

    panelActivated: (state) => {
      state.panelActive = true;
      state.unreadCount = 0;
    },

    panelDeactivated: (state) => {
      state.panelActive = false;
    },

    connectionStatusChanged: (state, action) => {
      state.connectionStatus = action.payload;
    },

    resetChat: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder.addCase(clearMeetingState, () => ({ ...initialState }));
  },
});

const { reducer, actions } = chatSlice;

export const {
  backlogLoaded,
  messageReceived,
  ownMessagePending,
  ownMessageConfirmed,
  ownMessageFailed,
  ownMessageDiscarded,
  ownMessageRetrying,
  panelActivated,
  panelDeactivated,
  connectionStatusChanged,
  resetChat,
} = actions;

export const selectChatMessages = (state) => state.chat.messages;
export const selectChatUnreadCount = (state) => state.chat.unreadCount;
export const selectChatJoinedAt = (state) => state.chat.joinedAt;
export const selectChatPanelActive = (state) => state.chat.panelActive;
export const selectChatConnectionStatus = (state) =>
  state.chat.connectionStatus;
export const selectChatBacklogLoaded = (state) => state.chat.backlogLoaded;

export default reducer;
